/**
 * Dashboard principal con estadísticas y resumen
 */
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Statistic, Typography, Space, Divider } from "antd";
import {
  ShopOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { useBranch } from "../context/BranchContext";
import api from "../api/endpoints";
import NutritionalInstructions from "../components/nutritional/NutritionalInstructions";

const { Title, Paragraph, Text } = Typography;

const Dashboard = () => {
  const { activeBranch } = useBranch();
  const [stats, setStats] = useState({
    branches: 0,
    ingredients: 0,
    recipes: 0,
    labels: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        
        // Cargar estadísticas
        const [branchesData, ingredientsData, recipesData] = await Promise.all([
          api.branches.list(1),
          api.products.list(1, { is_nutritional_ingredient: true, branch_id: activeBranch }),
          api.recipes.list(1, { recipe_type: "NUTRITIONAL_RECIPE", branch_id: activeBranch }),
        ]);

        setStats({
          branches: branchesData.count || 0,
          ingredients: ingredientsData.count || 0,
          recipes: recipesData.count || 0,
          labels: recipesData.results?.filter((r) => r.has_nutritional_calculation).length || 0,
        });
      } catch (error) {
        console.error("Error cargando estadísticas:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [activeBranch]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Title level={2} style={{ marginBottom: 8, color: "#1a1a1a" }}>
          Dashboard
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Resumen del sistema de gestión nutricional
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 12,
              border: "1px solid #e8e8e8",
              transition: "all 0.3s",
            }}
          >
            <Statistic
              title="Sucursales"
              value={stats.branches}
              prefix={<ShopOutlined style={{ color: "#1890ff" }} />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 12,
              border: "1px solid #e8e8e8",
              transition: "all 0.3s",
            }}
          >
            <Statistic
              title="Ingredientes Nutricionales"
              value={stats.ingredients}
              prefix={<ExperimentOutlined style={{ color: "#52c41a" }} />}
              loading={loading}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 12,
              border: "1px solid #e8e8e8",
              transition: "all 0.3s",
            }}
          >
            <Statistic
              title="Recetas"
              value={stats.recipes}
              prefix={<FileTextOutlined style={{ color: "#1890ff" }} />}
              loading={loading}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            hoverable
            style={{
              borderRadius: 12,
              border: "1px solid #e8e8e8",
              transition: "all 0.3s",
            }}
          >
            <Statistic
              title="Etiquetas Generadas"
              value={stats.labels}
              prefix={<TagOutlined style={{ color: "#ff4d4f" }} />}
              loading={loading}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card
        style={{
          borderRadius: 12,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          border: "none",
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Title level={4} style={{ marginBottom: 8, color: "#1a1a1a" }}>
              Bienvenido al Sistema de Gestión Nutricional
            </Title>
            <Paragraph style={{ fontSize: 15, color: "#595959", marginBottom: 0 }}>
              Este sistema te ayuda a crear las etiquetas nutricionales que necesitas para tus productos de helado. 
              Solo necesitas registrar tus ingredientes, crear tus recetas y el sistema calculará todo automáticamente. 
              Las etiquetas cumplen con los requisitos del Ministerio de Salud de Chile.
            </Paragraph>
          </div>
        </Space>
      </Card>

      <NutritionalInstructions />
    </div>
  );
};

export default Dashboard;

